import React from "react";
import RecognitionGuideLayout from "@/components/RecognitionGuideLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Gift, Trophy, Heart, Calendar, Users, MessageSquare, ArrowRight, ExternalLink, Plus, Minus } from "lucide-react";

export { Page }

function Page() {
  const recognitionTypes = [
    {
      title: "Performance-Based Recognition",
      description: "Acknowledges employees for achieving goals, exceeding expectations, or delivering exceptional results.",
      icon: Trophy,
      examples: [
        "Closing a big client deal",
        "Exceeding quarterly targets", 
        "Solving a complex technical challenge",
        "Leading a successful project launch"
      ],
      whyItMatters: "Performance recognition creates a direct link between effort and reward, motivating employees to maintain high standards and push for excellence.",
      links: [
        { text: "How to Effectively Recognize Good Employee Performance", url: "/blog/how-to-effectively-recognize-good-employee-performance" }
      ]
    },
    {
      title: "Values-Based Recognition",
      description: "Celebrates employees who demonstrate company values in their daily work, regardless of specific outcomes.",
      icon: Heart,
      examples: [
        "Going above and beyond to help a colleague",
        "Demonstrating integrity in a difficult situation",
        "Showing exceptional customer service",
        "Collaborating effectively across departments"
      ],
      whyItMatters: "Values-based recognition reinforces culture and helps employees understand what behaviors matter most to the organization.",
      links: []
    },
    {
      title: "Milestone Recognition",
      description: "Honors significant events in an employee's journey, from work anniversaries to promotions and personal achievements.",
      icon: Calendar,
      examples: [
        "Work anniversaries (1, 5, 10+ years)",
        "Promotions and role changes",
        "Professional certifications or training completions",
        "Personal milestones (birthdays, graduations)"
      ],
      whyItMatters: "Milestone recognition shows long-term appreciation and helps employees feel their growth and commitment are valued.",
      links: [
        { text: "Best Practices for Celebrating Milestones and Anniversaries", url: "/blog/best-practices-for-celebrating-milestones-and-anniversaries" },
        { text: "Employee Milestones and the Significance of Long-term Recognition", url: "/blog/employee-milestones-and-the-significance-of-long-term-recognition" }
      ]
    },
    {
      title: "Peer-to-Peer Recognition",
      description: "Enables colleagues to recognize each other directly, creating a culture where appreciation flows in all directions.",
      icon: Users,
      examples: [
        "Team member helping with a tight deadline",
        "Colleague sharing valuable knowledge or expertise",
        "Cross-departmental collaboration",
        "Mentoring and supporting new team members"
      ],
      whyItMatters: "Peer recognition builds stronger team bonds and ensures appreciation isn't limited to manager-employee relationships.",
      links: [
        { text: "Your Complete Guide to Getting Started with Peer Recognition", url: "/blog/your-complete-guide-to-getting-started-with-peer-recognition" },
        { text: "The Link Between Employee Recognition and Team Collaboration", url: "/blog/the-link-between-employee-recognition-and-team-collaboration" }
      ]
    },
    {
      title: "Social Recognition",
      description: "Public acknowledgment that increases visibility and allows the broader organization to celebrate achievements together.",
      icon: MessageSquare,
      examples: [
        "Company-wide announcements",
        "Recognition in team meetings",
        "Social media shoutouts",
        "Internal newsletters and communications"
      ],
      whyItMatters: "Social recognition amplifies impact by making achievements visible to a wider audience and inspiring others.",
      links: [
        { text: "Discover Employee Awards That Celebrate Outstanding Achievements", url: "https://joinassembly.com/employee-recognition/awards" }
      ]
    },
    {
      title: "Monetary & Tangible Rewards",
      description: "Recognition paired with financial incentives, gift cards, experiences, or physical items that provide lasting value.",
      icon: Gift,
      examples: [
        "Spot bonuses for exceptional work",
        "Gift cards to favorite restaurants or retailers",
        "Experience rewards (concert tickets, spa days)",
        "Company swag and branded items"
      ],
      whyItMatters: "Tangible rewards create memorable experiences and provide concrete value that employees can enjoy beyond the workplace.",
      links: [
        { text: "50 Best Personalized Reward Ideas for Recognizing Employees", url: "/blog/50-best-personalized-reward-ideas-for-recognizing-employees" }
      ]
    }
  ];

  return (
    <RecognitionGuideLayout
      title="Types of Employee Recognition and Rewards"
      description="Explore different types of employee recognition and reward programs. Learn about monetary vs non-monetary rewards, peer recognition, milestone celebrations, and how to choose the right approach."
      currentStep={3}
    >
      <div className="space-y-12">
        {/* Header */}
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium border border-primary/20">
            <Gift className="w-4 h-4" />
            Step 3 of 6
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
            Types of Employee Recognition and Rewards
          </h1>
          
          <div className="space-y-4">
            <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
              Recognition is most impactful when it reflects the many ways employees contribute. Some thrive on public praise, others value private appreciation, and many respond best when recognition is paired with meaningful rewards. A well-designed program blends multiple types of recognition so every employee feels seen.
            </p>
            
            <p className="text-muted-foreground max-w-4xl leading-relaxed">
              Below are the six core categories that drive results, with best practices and resources for each. When you're ready to implement these approaches, explore our curated <a href="https://joinassembly.com/best/employee-recognition-software/top" className="text-primary hover:text-primary/80 underline">Top 20 Employee Recognition Software</a> platforms to find the right technology for your team.
            </p>
          </div>
        </div>

        {/* Recognition Types */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Six Core Types of Recognition 
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Each type serves a different purpose and resonates with different employees
            </p>
          </div>

          <div className="grid gap-8">
            {recognitionTypes.map((type, index) => {
              const IconComponent = type.icon;
              return (
                <Card key={index} className="border-border/60 bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="flex items-start">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                          <IconComponent className="w-6 h-6 text-primary" />
                        </div>
                      </div>
                      
                      <div className="flex-1 space-y-4">
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">
                            {index + 1}. {type.title}
                          </h3>
                          <p className="text-muted-foreground leading-relaxed mb-4">
                            {type.description}
                          </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-medium text-foreground mb-3">Examples:</h4>
                            <ul className="space-y-2">
                              {type.examples.map((example, exampleIndex) => (
                                <li key={exampleIndex} className="flex items-start gap-2 text-sm">
                                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></div>
                                  <span className="text-muted-foreground">{example}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-medium text-foreground mb-3">Why it matters:</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {type.whyItMatters}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row gap-2 pt-2">
                          {type.links.map((link, linkIndex) => (
                            <div key={linkIndex} className="flex items-center gap-2 text-sm">
                              <ExternalLink className="w-4 h-4 text-primary shrink-0" />
                              <a 
                                href={link.url} 
                                className="text-primary hover:text-primary/80 transition-colors underline underline-offset-2"
                              >
                                {link.text}
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Bringing It All Together */}
        <section>
          <Card className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-primary/20 backdrop-blur-sm">
            <CardContent className="p-8 text-center space-y-6">
              <div className="space-y-3">
                <h3 className="text-2xl font-bold text-foreground">
                  Bringing It All Together
                </h3>
                
                <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  The most successful recognition programs blend multiple types. A program that combines performance-based recognition, values alignment, milestones, peer-to-peer appreciation, and rewards creates more touchpoints for employees to feel valued.
                </p>
              </div>
              
              <p className="text-muted-foreground max-w-2xl mx-auto">
                With Assembly, you can manage them all in one platform-whether that's{" "}
                <a href="/employee-recognition/peer-to-peer" className="text-primary hover:text-primary/80 underline">
                  peer-to-peer recognition
                </a>, automated{" "}
                <a href="/employee-recognition/milestones" className="text-primary hover:text-primary/80 underline">
                  milestones
                </a>, or customizable rewards.
              </p>
              
              <div className="flex items-center gap-2 text-sm justify-center pt-4">
                <ExternalLink className="w-4 h-4 text-primary" />
                <a href="/blog/main-types-of-employee-recognition-to-implement-at-work" className="text-primary hover:text-primary/80 underline">
                  Main Types of Employee Recognition to Implement at Work
                </a>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* FAQ Section */}
        <section className="space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Common questions about recognition types and implementation
            </p>
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            {[
              {
                question: "What's the most effective type of employee recognition?",
                answer: "The most effective recognition combines multiple types: peer-to-peer for daily appreciation, milestone recognition for achievements, values-based for culture reinforcement, and meaningful rewards. A balanced approach works best."
              },
              {
                question: "Should recognition be public or private?",
                answer: "Both have their place. Public recognition increases visibility and motivates others, while private recognition can be more meaningful for certain personalities. Offer options and let employees choose their preference."
              },
              {
                question: "How do you ensure recognition is equitable across all employees?",
                answer: "Track recognition data by department, role, and demographics. Set guidelines for managers, encourage peer-to-peer recognition, and use automated systems for milestone celebrations to ensure no one is overlooked."
              },
              {
                question: "What's the right balance between monetary and non-monetary recognition?",
                answer: "Research shows the most impactful recognition combines both. Start with meaningful non-monetary recognition (specific praise, career development) and add modest monetary rewards for special achievements and milestones."
              }
            ].map((faq, index) => (
              <Collapsible key={index}>
                <Card className="border-border/60 bg-card/50 backdrop-blur-sm">
                  <CollapsibleTrigger className="w-full group">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-left flex items-center justify-between">
                        <span className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                          {faq.question}
                        </span>
                        <div className="flex items-center">
                          <Plus className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors group-data-[state=open]:hidden" />
                          <Minus className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors group-data-[state=closed]:hidden" />
                        </div>
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        </section>

        {/* Next Steps CTA */}
        <Card className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-primary/20 backdrop-blur-sm">
          <CardContent className="p-8 text-center space-y-6">
            <h3 className="text-2xl font-bold text-foreground">
              Ready to Build Your Program?
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Now that you understand the different types of recognition, learn how to build and implement a successful recognition program that combines these approaches effectively.
            </p>
            <Button size="lg" asChild>
              <a href="/employee-recognition-program-guide-assembly/building-a-successful-employee-recognition-program-assembly" className="flex items-center gap-2">
                Build Your Recognition Program
                <ArrowRight className="w-4 h-4" />
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>
    </RecognitionGuideLayout>
  );
}